import { WorkflowChatTransport } from "@workflow/ai";
import type { UIMessage } from "ai";

import { supabase } from "~/store/initializeStore";

import { getClientWebApiBaseUrl, type SalesAgentModelOptionId } from "./constants";
import { clearActiveRun, loadActiveRun, saveActiveRun } from "./workflowRunStore";

// TRE-1911 — transport durable dla czatu admina, lustrzany wobec mobilnego
// (apps/mobile/src/screens/sales/agent/sales-agent-transport.ts). Run id + HMAC
// token z odpowiedzi POST idą do localStorage, żeby zerwany stream dało się
// wznowić przez GET /chat-workflow/[runId]/stream. Token runa jedzie w nagłówku,
// nie w query — transport sam dokleja `?startIndex=` przy wznowieniu.

const getAuthHeader = async (): Promise<Record<string, string>> => {
  const token = (await supabase.auth.getSession()).data.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

type CreateTransportOptions = {
  getActiveSessionId: () => string | null;
  getUserId: () => string | null;
  getModelId: () => SalesAgentModelOptionId;
  getDisableObservationalMemory: () => boolean;
};

export const createSalesAgentWorkflowTransport = ({
  getActiveSessionId,
  getUserId,
  getModelId,
  getDisableObservationalMemory,
}: CreateTransportOptions) =>
  new WorkflowChatTransport<UIMessage>({
    api: `${getClientWebApiBaseUrl()}/api/sales-agent/chat-workflow`,
    prepareSendMessagesRequest: async ({ api, body, credentials, headers, id, messages, trigger, messageId }) => {
      const authHeader = await getAuthHeader();
      const latestMessage = messages.at(-1);
      const disableObservationalMemory = getDisableObservationalMemory();

      return {
        api,
        body: {
          ...body,
          id,
          sessionId: getActiveSessionId() ?? undefined,
          model: getModelId(),
          messages: latestMessage ? [latestMessage] : [],
          trigger,
          messageId,
          debug: disableObservationalMemory ? { disableObservationalMemory } : undefined,
        },
        credentials,
        headers: { ...headers, ...authHeader },
      };
    },
    onChatSendMessage: (response) => {
      const runId = response.headers.get("x-workflow-run-id");
      const runToken = response.headers.get("x-workflow-run-token");
      const userId = getUserId();
      if (runId && runToken && userId) {
        saveActiveRun({ runId, runToken, sessionId: getActiveSessionId(), userId });
      }
    },
    onChatEnd: () => {
      clearActiveRun();
    },
    prepareReconnectToStreamRequest: async ({ credentials, headers }) => {
      const activeRun = loadActiveRun(getUserId());
      if (!activeRun) throw new Error("No active sales agent run to reconnect to");

      const authHeader = await getAuthHeader();

      return {
        api: `${getClientWebApiBaseUrl()}/api/sales-agent/chat-workflow/${encodeURIComponent(activeRun.runId)}/stream`,
        credentials,
        headers: {
          ...headers,
          ...authHeader,
          "x-sales-agent-run-token": activeRun.runToken,
        },
      };
    },
  });

/**
 * Anuluje run po stronie serwera. Samo stop() ucina lokalny stream — workflow
 * dalej by generował (i bilingował), a marker activeRun na sesji kazałby innym
 * klientom wznawiać odpowiedź, której nikt już nie chce.
 */
export const cancelSalesAgentWorkflowRun = async ({
  runId,
  runToken,
  sessionId,
}: {
  runId: string;
  runToken: string;
  sessionId: string | null;
}): Promise<void> => {
  const authHeader = await getAuthHeader();

  const response = await fetch(
    `${getClientWebApiBaseUrl()}/api/sales-agent/chat-workflow/${encodeURIComponent(runId)}/cancel`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...authHeader,
        "x-sales-agent-run-token": runToken,
      },
      body: JSON.stringify({ sessionId: sessionId ?? undefined }),
    },
  );

  if (!response.ok) {
    throw new Error(`Cancel request failed with status ${response.status}`);
  }
};
